import {inject, Injectable} from '@angular/core';
import {HttpClient} from '@angular/common/http';
import {Observable, of, tap} from 'rxjs';
import {baseUrl} from '../configs/baseUrl';
import {isInAppWebView} from '../utils/webview.utils';
import {isPresignedStorageUrl} from '../interceptors/ic-http-interceptor.interceptor';

export interface IDocumentDownload {
  url: string;
  name: string;
  fileName?: string;
  isDownloading: boolean;
  openInNewWindow?: boolean;
}

interface IResourceAccessToken {
  token: string;
  expiresIn: number;
}

@Injectable({
  providedIn: 'root',
})
export class DocumentService {
  private httpClient = inject(HttpClient);

  downloadDocument(
    url: string,
    doc: string,
    download: boolean = true,
  ): Observable<unknown> {
    // WebViews can't download or open blob: URLs, so the objectUrl below silently does
    // nothing there. Hand the file to the platform as a real navigation instead.
    if (isInAppWebView()) {
      // A pre-signed URL is already authorised, so navigate to it as-is.
      if (isPresignedStorageUrl(url)) {
        window.location.assign(url);
        return of(null);
      }
      return this.navigateToDocument(url);
    }

    return this.httpClient.get(url, {responseType: 'blob'}).pipe(
      tap((blob) => {
        const objectUrl = URL.createObjectURL(blob);
        try {
          if (download) {
            const link = document.createElement('a');
            link.href = objectUrl;
            link.download = doc;
            link.rel = 'noopener noreferrer';
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
          } else {
            window.open(objectUrl, '_blank', 'noopener,noreferrer');
          }
        } finally {
          setTimeout(() => URL.revokeObjectURL(objectUrl), 0);
        }
      }),
    );
  }

  private navigateToDocument(url: string): Observable<IResourceAccessToken> {
    const target = new URL(url, window.location.origin);

    return this.httpClient
      .post<IResourceAccessToken>(`${baseUrl()}/resources/download-token`, {
        path: target.pathname,
      })
      .pipe(
        tap(({token}) => {
          target.searchParams.set('token', token);
          window.location.assign(target.toString());
        }),
      );
  }
}
